import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  FormHelperText,
  Grid,
  Input,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import ClearIcon from "@mui/icons-material/Clear";
import { useFormik } from "formik";
import { UploadMedia } from "../../utils/mediaUpload";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { showError, showToast } from "../../constants/toasts";
import { DOCUMENT_TYPE } from "../../utils/enums";
import Loader from "../../constants/Loader";
import CloseIcon from "@mui/icons-material/Close";
import {
  useLazyGetCustomerByIdQuery,
  useUpdateCustomerMutation,
} from "../../services/customer";
import { CustomerDocs } from "../../types/General";

type props = {
  userId: string | undefined;
  setValueTabs: Dispatch<SetStateAction<number>>;
  view?: boolean;
  mode?: string | undefined;
  setOpenDocView: Dispatch<SetStateAction<boolean>>;
  setDocViewImage: Dispatch<SetStateAction<string>>;
};

const CompanyDocumentation = ({
  setValueTabs,
  userId,
  view,
  mode,
  setOpenDocView,
  setDocViewImage,
}: props) => {
  const { id } = useParams();
  const location = useLocation();
  const { state } = location;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [ByIdDetails, setByIdDetails] = useState<CustomerDocs[]>();
  const [updateDoc] = useUpdateCustomerMutation();
  const [getCustomerById] = useLazyGetCustomerByIdQuery();

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const files = event.target.files;

    const file = files?.length ? files[0] : null;

    if (!file) {
      return;
    }

    const imageUrl = (await UploadMedia(file)) as any;

    if (imageUrl?.data) {
      formik.setFieldValue(type, imageUrl?.data);
    }
  };

  const fetchDataById = async (id: any) => {
    setIsLoading(true);
    try {
      const response = await getCustomerById({
        id,
      }).unwrap();
      setIsLoading(false);

      if (response?.statusCode === 200) {
        setByIdDetails(response?.data?.document || null);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      passportExpiry: "",
      frontImg_pass: "",
      backImg_pass: "",

      emirateExpiry: "",
      frontImg_emirate: "",
      backImg_emirate: "",

      businessExpiry: "",
      frontImg_business: "",
      backImg_business: "",
    },

    validationSchema: Yup.object({
      passportExpiry: Yup.string().required("Expiry date is required"),
      frontImg_pass: Yup.string().required("Front image is required"),
      backImg_pass: Yup.string().required("Back image is required"),

      emirateExpiry: Yup.string().required("Expiry date is required"),
      frontImg_emirate: Yup.string().required("Front image is required"),
      backImg_emirate: Yup.string().required("Back image is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      setSubmitting(true);

      let body = {
        document: [
          {
            type: DOCUMENT_TYPE.PASS_P0RT,
            docExpiryDate: formik.values.passportExpiry
              ? moment(formik.values.passportExpiry)
                  .utc()
                  .format("YYYY-MM-DDTHH:mm:ss[Z]")
              : "",
            docFrontImage: formik.values.frontImg_pass,
            docBackImage: formik.values.backImg_pass,
          },
          {
            type: DOCUMENT_TYPE.EMIRATES_ID,
            docExpiryDate: formik.values.emirateExpiry
              ? moment(formik.values.emirateExpiry)
                  .utc()
                  .format("YYYY-MM-DDTHH:mm:ss[Z]")
              : "",
            docFrontImage: formik.values.frontImg_emirate,
            docBackImage: formik.values.backImg_emirate,
          },

          ...(formik.values.frontImg_business
            ? [
                {
                  type: DOCUMENT_TYPE.BUSINESS_LICENSE,
                  docExpiryDate: formik.values.businessExpiry
                    ? moment(formik.values.businessExpiry)
                        .utc()
                        .format("YYYY-MM-DDTHH:mm:ss[Z]")
                    : "",
                  docFrontImage: formik.values.frontImg_business,
                },
              ]
            : []),
        ],
        userId,
        // isProfileCompleted: true,
      };

      try {
        setIsLoading(true);
        const response = await updateDoc(body).unwrap();
        if (response.statusCode === 200) {
          setIsLoading(false);

          if (location.pathname?.includes("/edit")) {
            showToast("Documents updated successfully");
          } else {
            showToast("Documents added successfully");
          }

          if (mode === "customer") {
            navigate("/manage-customers");
          } else {
            navigate("/manage-maintenance-team");
          }
        } else {
          showError("Failed to update company");
        }
      } catch (error) {
        setIsLoading(false);
        console.error("Error updating user:", error);
        showError("Error updating user. Please try again.");
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }

      // if (id) {
      //   try {
      //     setIsLoading(true);
      //     const response = await editUSERbyId({
      //       id,
      //       body: body,
      //       isDocEdit: true,
      //     }).unwrap();
      //     if (response.statusCode === 200) {
      //       setIsLoading(false);
      //       showToast("Company updated successfully");
      //       navigate("/manage-company");
      //     } else {
      //       showError("Failed to update user");
      //     }
      //   } catch (error: any) {
      //     setIsLoading(false);
      //     console.error("Error updating user:", error);
      //     showError(error?.data?.message);
      //   } finally {
      //     setIsLoading(false);
      //     setSubmitting(false);
      //   }
      // } else {

      // }
    },
  });

  useEffect(() => {
    if (ByIdDetails && ByIdDetails.length) {
      ByIdDetails.forEach((item: any) => {
        const documentType: number = item?.type ? item?.type : 0;
        if (documentType === DOCUMENT_TYPE.PASS_P0RT) {
          formik.setFieldValue(`frontImg_pass`, item?.docFrontImage);
          formik.setFieldValue(`backImg_pass`, item?.docBackImage);
          formik.setFieldValue(
            `passportExpiry`,
            item?.docExpiryDate
              ? moment(item?.docExpiryDate).format("YYYY-MM-DD")
              : ""
          );
        } else if (documentType === DOCUMENT_TYPE.EMIRATES_ID) {
          formik.setFieldValue(`frontImg_emirate`, item?.docFrontImage);
          formik.setFieldValue(`backImg_emirate`, item?.docBackImage);
          formik.setFieldValue(
            `emirateExpiry`,
            item?.docExpiryDate
              ? moment(item?.docExpiryDate).format("YYYY-MM-DD")
              : ""
          );
        } else if (documentType === DOCUMENT_TYPE.BUSINESS_LICENSE) {
          formik.setFieldValue(`frontImg_business`, item?.docFrontImage);
          formik.setFieldValue(
            `businessExpiry`,
            item?.docExpiryDate
              ? moment(item?.docExpiryDate).format("YYYY-MM-DD")
              : ""
          );
        }
      });
    }
  }, [ByIdDetails]);

  useEffect(() => {
    if (id) {
      fetchDataById(id);
    }
  }, []);

  return (
    <div className="main_layout">
      <Loader isLoad={isLoading} />
      <form onSubmit={formik.handleSubmit}>
        {/* // passport id */}
        {view && !formik.values.frontImg_pass ? null : (
          <div className="single_doc">
            <Grid container spacing={2}>
              <Grid item xs={12} className="heading">
                <Typography className="document_head" variant="h5">
                  Passport
                </Typography>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <div className="control_group">
                  <Typography className="custom_label">Expiry date</Typography>
                  <TextField
                    disabled={view}
                    hiddenLabel
                    type={"date"}
                    className="text_field"
                    variant="outlined"
                    fullWidth
                    inputProps={{
                      min: new Date(
                        new Date().getFullYear(),
                        new Date().getMonth() + 1,
                        new Date().getDate()
                      )
                        .toISOString()
                        .split("T")[0],
                    }}
                    placeholder="Enter expiry date"
                    value={formik.values.passportExpiry}
                    onChange={(e) =>
                      formik.setFieldValue("passportExpiry", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.passportExpiry &&
                      Boolean(formik.errors.passportExpiry)
                    }
                    helperText={
                      formik.touched.passportExpiry &&
                      formik.errors.passportExpiry
                    }
                  />
                </div>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <div className="control_group">
                  <Typography className="custom_label">Front Side</Typography>
                  <Box className="docs_div v2">
                    {formik.values.frontImg_pass ? (
                      <div
                        className="upload_image no_radius v2"
                        onClick={() => {
                          setOpenDocView(true);
                          setDocViewImage(formik.values.frontImg_pass);
                        }}
                      >
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia
                              component="img"
                              image={formik.values.frontImg_pass}
                              alt="photo"
                            />
                          </figure>
                          {view ? null : (
                            <CloseIcon
                              onClick={() =>
                                formik.setFieldValue("frontImg_pass", "")
                              }
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <label
                        className="upload_image no_radius v2"
                        htmlFor="icon-button-file"
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(event: any) =>
                            handleImageUpload(event, "frontImg_pass")
                          }
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt="icon"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="icon"
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    )}
                  </Box>
                  {formik.touched.frontImg_pass &&
                    formik.errors.frontImg_pass && (
                      <Typography
                        className="text_field"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {formik.errors.frontImg_pass}
                      </Typography>
                    )}
                </div>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <div className="control_group">
                  <Typography className="custom_label">Back Side</Typography>
                  <Box className="docs_div v2">
                    {formik.values.backImg_pass ? (
                      <div
                        className="upload_image no_radius v2"
                        onClick={() => {
                          setOpenDocView(true);
                          setDocViewImage(formik.values.backImg_pass);
                        }}
                      >
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia
                              component="img"
                              image={formik.values.backImg_pass}
                              alt="photo"
                            />
                          </figure>
                          {view ? null : (
                            <CloseIcon
                              onClick={() =>
                                formik.setFieldValue("backImg_pass", "")
                              }
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <label
                        className="upload_image no_radius v2"
                        htmlFor="icon-button-file"
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(event: any) =>
                            handleImageUpload(event, "backImg_pass")
                          }
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt="icon"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="icon"
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    )}
                  </Box>
                  {formik.touched.backImg_pass &&
                    formik.errors.backImg_pass && (
                      <Typography
                        className="text_field"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {formik.errors.backImg_pass}
                      </Typography>
                    )}
                </div>
              </Grid>
            </Grid>
          </div>
        )}

        {/* // passport id */}

        {/* // emirates id */}
        {view && !formik.values.frontImg_emirate ? null : (
          <div className="single_doc">
            <Grid container spacing={2}>
              <Grid item xs={12} className="heading">
                <Typography className="document_head" variant="h5">
                  Emirate Id
                </Typography>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <div className="control_group">
                  <Typography className="custom_label">Expiry date</Typography>
                  <TextField
                    disabled={view}
                    hiddenLabel
                    type={"date"}
                    className="text_field"
                    variant="outlined"
                    fullWidth
                    inputProps={{
                      min: new Date(
                        new Date().getFullYear(),
                        new Date().getMonth() + 1,
                        new Date().getDate()
                      )
                        .toISOString()
                        .split("T")[0],
                    }}
                    placeholder="Enter expiry date"
                    value={formik.values.emirateExpiry}
                    onChange={(e) =>
                      formik.setFieldValue("emirateExpiry", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.emirateExpiry &&
                      Boolean(formik.errors.emirateExpiry)
                    }
                    helperText={
                      formik.touched.emirateExpiry &&
                      formik.errors.emirateExpiry
                    }
                  />
                  {/* {formik.touched.emirateExpiry &&
                  formik.errors.emirateExpiry && (
                    <Typography
                      className="text_field"
                      style={{ color: "red", fontSize: "12px" }}
                    >
                      {formik.errors.emirateExpiry}
                    </Typography>
                  )} */}
                </div>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <div className="control_group">
                  <Typography className="custom_label">Front Side</Typography>
                  <Box className="docs_div v2">
                    {formik.values.frontImg_emirate ? (
                      <div
                        className="upload_image no_radius v2"
                        onClick={() => {
                          setOpenDocView(true);
                          setDocViewImage(formik.values.frontImg_emirate);
                        }}
                      >
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia
                              component="img"
                              image={formik.values.frontImg_emirate}
                              alt="photo"
                            />
                          </figure>
                          {view ? null : (
                            <CloseIcon
                              onClick={() =>
                                formik.setFieldValue("frontImg_emirate", "")
                              }
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <label
                        className="upload_image no_radius v2"
                        htmlFor="icon-button-file"
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(event: any) =>
                            handleImageUpload(event, "frontImg_emirate")
                          }
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt="icon"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="icon"
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    )}
                  </Box>
                  {formik.touched.frontImg_emirate &&
                    formik.errors.frontImg_emirate && (
                      <Typography
                        className="text_field"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {formik.errors.frontImg_emirate}
                      </Typography>
                    )}
                </div>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <div className="control_group">
                  <Typography className="custom_label">Back Side</Typography>
                  <Box className="docs_div v2">
                    {formik.values.backImg_emirate ? (
                      <div
                        className="upload_image no_radius v2"
                        onClick={() => {
                          setOpenDocView(true);
                          setDocViewImage(formik.values.backImg_emirate);
                        }}
                      >
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia
                              component="img"
                              image={formik.values.backImg_emirate}
                              alt="photo"
                            />
                          </figure>
                          {view ? null : (
                            <CloseIcon
                              onClick={() =>
                                formik.setFieldValue("backImg_emirate", "")
                              }
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <label
                        className="upload_image no_radius v2"
                        htmlFor="icon-button-file"
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(event: any) =>
                            handleImageUpload(event, "backImg_emirate")
                          }
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt="icon"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="icon"
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    )}
                  </Box>
                  {formik.touched.backImg_emirate &&
                    formik.errors.backImg_emirate && (
                      <Typography
                        className="text_field"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {formik.errors.backImg_emirate}
                      </Typography>
                    )}
                </div>
              </Grid>
            </Grid>
          </div>
        )}

        {/* // emirates id */}

        {/* // business  id */}

        {view && !formik.values.frontImg_business ? null : (
          <div className="single_doc">
            <Grid container spacing={2}>
              <Grid item xs={12} className="heading">
                <Typography className="document_head" variant="h5">
                  Business License(Optional)
                </Typography>
              </Grid>
              <Grid item lg={4} md={4} sm={6} xs={12}>
                <div className="control_group">
                  <Typography className="custom_label">Expiry date</Typography>
                  <TextField
                    disabled={view}
                    hiddenLabel
                    type={"date"}
                    className="text_field"
                    variant="outlined"
                    fullWidth
                    inputProps={{
                      min: new Date(
                        new Date().getFullYear(),
                        new Date().getMonth() + 1,
                        new Date().getDate()
                      )
                        .toISOString()
                        .split("T")[0],
                    }}
                    placeholder="Enter expiry date"
                    value={formik.values.businessExpiry}
                    onChange={(e) =>
                      formik.setFieldValue("businessExpiry", e.target.value)
                    }
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.businessExpiry &&
                      Boolean(formik.errors.businessExpiry)
                    }
                    helperText={
                      formik.touched.businessExpiry &&
                      formik.errors.businessExpiry
                    }
                  />
                </div>
              </Grid>

              <Grid item lg={4} md={4} sm={6} xs={12}>
                <div className="control_group">
                  <Typography className="custom_label">Front Side</Typography>
                  <Box className="docs_div v2">
                    {formik.values.frontImg_business ? (
                      <div
                        className="upload_image no_radius v2"
                        onClick={() => {
                          setOpenDocView(true);
                          setDocViewImage(formik.values.frontImg_business);
                        }}
                      >
                        <div className="upload_image_holder">
                          <figure>
                            <CardMedia
                              component="img"
                              image={formik.values.frontImg_business}
                              alt="photo"
                            />
                          </figure>
                          {view ? null : (
                            <CloseIcon
                              onClick={() =>
                                formik.setFieldValue("frontImg_business", "")
                              }
                            />
                          )}
                        </div>
                      </div>
                    ) : (
                      <label
                        className="upload_image no_radius v2"
                        htmlFor="icon-button-file"
                      >
                        <Input
                          sx={{ display: "none" }}
                          id="icon-button-file"
                          type="file"
                          inputProps={{
                            accept: "image/png,image/jpeg",
                          }}
                          onChange={(event: any) =>
                            handleImageUpload(event, "frontImg_business")
                          }
                        />
                        <span className="upload_image_holder">
                          <figure className="add_img">
                            <img
                              src="/static/images/placeholder.png"
                              alt="icon"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="icon"
                              />
                            </span>
                          </figure>
                        </span>
                      </label>
                    )}
                  </Box>
                  {formik.touched.frontImg_business &&
                    formik.errors.frontImg_business && (
                      <Typography
                        className="text_field"
                        style={{ color: "red", fontSize: "12px" }}
                      >
                        {formik.errors.frontImg_business}
                      </Typography>
                    )}
                </div>
              </Grid>
            </Grid>
          </div>
        )}
        {/* // business  id */}
        {view ? null : (
          <div className="form_btn">
            <Button size="large" type="submit" className="btn btn_primary">
              Submit
            </Button>
          </div>
        )}
      </form>
    </div>
  );
};

export default CompanyDocumentation;
